import React from 'react';
import { Clipboard, ClipboardCheck, Email } from '@good/icons';
import { ActionIcon, Box, Button, Paper, Stack, TextInput, Text, ThemeIcon, Alert } from '@good/ui/core';
import { useClipboard } from '@good/ui/hooks';
import { GOODSIGN_URL } from 'config/app-config';
import { reactTrpc } from 'utilities/react-trpc';
import { CreateAgreementLinkModalBodySkeleton } from './create-agreement-link-modal-body-skeleton';

interface CreateAgreementLinkModalBodyProps {
  historyId: string;
}

const notEmptyString = (value: string | null | undefined): value is string => {
  return value !== null && value !== undefined && value.trim() !== '';
};

export const CreateAgreementLinkModalBody = ({ historyId }: CreateAgreementLinkModalBodyProps) => {
  const clipboard = useClipboard();
  const { data, isLoading, isError } = reactTrpc.customers.userServiceAgreementHistories.fetchForSend.useQuery({
    userServiceAgreementHistoryId: historyId,
  });
  const linkCode = data?.signLinkCode;
  const linkUrl = `${GOODSIGN_URL}/?code=${linkCode}`;

  const getMailClientHref = (subject: string, body: string) => {
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (isLoading) {
    return <CreateAgreementLinkModalBodySkeleton />;
  }

  if (isError) {
    return (
      <Alert title='Error' color='red'>
        Failed to load the agreement link. Please try again later.
      </Alert>
    );
  }

  if (!notEmptyString(linkCode)) {
    return null;
  }

  return (
    <Stack gap='lg'>
      <Paper withBorder p='md'>
        <Stack>
          <ThemeIcon variant='default' size='lg'>
            <Email />
          </ThemeIcon>
          <Stack gap={4}>
            <Text fw={600}>Send via mail client</Text>
            <Text c='dimmed' size='sm' lh={1.2}>
              Share this link with your customer using your default mail client. A prefilled email with the link will be
              created.
            </Text>
          </Stack>
          <Box>
            <Button
              variant='filled'
              component='a'
              href={getMailClientHref(
                'Service Agreement Link',
                `Please review and sign the service agreement using the following link: ${linkUrl}`,
              )}
              target='_blank'
              rel='noopener noreferrer'
              td='none'
            >
              Open mail client
            </Button>
          </Box>
        </Stack>
      </Paper>
      <Paper withBorder p='md'>
        <Stack>
          <ThemeIcon variant='default' size='lg'>
            <ClipboardCheck />
          </ThemeIcon>
          <Stack gap={4}>
            <Text fw={600}>Copy link</Text>
            <Text c='dimmed' size='sm' lh={1.2}>
              Share this link with your customer however your prefer to review and sign the service agreement.
            </Text>
          </Stack>
          <TextInput
            readOnly
            value={linkUrl}
            rightSection={
              <ActionIcon onClick={() => clipboard.copy(linkUrl)} variant='default'>
                {clipboard.copied ? <ClipboardCheck /> : <Clipboard />}
              </ActionIcon>
            }
          />
        </Stack>
      </Paper>
    </Stack>
  );
};
